/**
 * Store: Daily deal
 * -----------------------------------------------------------------------------
 * Active Daily Deal for the current market.
 * - Entries come from the `daily_deal` metaobject via `cnvs.dailyDeals`.
 * - Switches to the next entry at its start/end time without a reload.
 * - Only JSDoc custom parameters, ignore module state or object.
 *
 */
import { defineStore } from 'pinia';

// setTimeout overflows above ~24.8 days, so long waits are split up.
const maxTimeoutDelay = 2 ** 31 - 1;

const useDailyDealStore = defineStore('daily-deal', {
  state: () => ({
    entries: window.cnvs?.dailyDeals || [],
    now: Date.now(),
    switchTimer: null,
  }),

  getters: {
    /**
     * Get the active entry. If entries overlap, the latest start wins.
     * @returns {Object|null}
     */
    activeDailyDeal(state) {
      return state.entries
        .filter(
          (entry) => entry.startsAt <= state.now && state.now < entry.endsAt,
        )
        .reduce(
          (latest, entry) =>
            !latest || entry.startsAt > latest.startsAt ? entry : latest,
          null,
        );
    },

    /**
     * Get if a product or variant id is part of the active entry.
     * @returns {Function}
     */
    isInActiveDailyDeal() {
      return ({ productId, variantId }) => {
        const deal = this.activeDailyDeal;

        if (!deal) {
          return false;
        }

        return (
          (deal.products || []).map(String).includes(String(productId)) ||
          (deal.variants || []).map(String).includes(String(variantId))
        );
      };
    },
  },

  actions: {
    /**
     * Start the timer that updates `now` at the next start or end time.
     * - Safe to call from every component, only one timer runs.
     */
    startSwitchTimer() {
      if (this.switchTimer) {
        return;
      }

      const nextSwitch = this.entries
        .flatMap((entry) => [entry.startsAt, entry.endsAt])
        .filter((time) => time > this.now)
        .sort((a, b) => a - b)[0];

      if (!nextSwitch) {
        return;
      }

      this.switchTimer = setTimeout(
        () => {
          this.switchTimer = null;
          this.now = Date.now();
          this.startSwitchTimer();
        },
        Math.min(nextSwitch - this.now, maxTimeoutDelay),
      );
    },
  },
});

export default useDailyDealStore;
